import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { PrimitiveRef } from "../types";

export function TextInput(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="ctl">
      <span>{props.label}</span>
      <input value={props.value} onChange={(e) => props.onChange(e.target.value)} />
    </label>
  );
}

export function NumberInput(props: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  step?: number;
  min?: number;
}) {
  return (
    <label className="ctl">
      <span>{props.label}</span>
      <input
        type="number"
        step={props.step ?? 1}
        min={props.min}
        value={props.value}
        onChange={(e) => {
          // Guard: Number("") === 0, but mid-edit the user may clear the field producing NaN.
          // Fall back to 0 so the definition state never holds NaN (determinism guard).
          const n = Number(e.target.value);
          props.onChange(Number.isFinite(n) ? n : 0);
        }}
      />
    </label>
  );
}

export function CheckboxInput(props: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="ctl ctl-check">
      <input
        type="checkbox"
        checked={props.checked}
        onChange={(e) => props.onChange(e.target.checked)}
      />
      <span>{props.label}</span>
    </label>
  );
}

export function SelectInput(props: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  allowEmpty?: boolean;
}) {
  return (
    <label className="ctl">
      <span>{props.label}</span>
      <select value={props.value} onChange={(e) => props.onChange(e.target.value)}>
        {props.allowEmpty && <option value="">—</option>}
        {props.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

/** A JSON object sub-editor: free-form params validated as you type. */
export function JsonInput(props: {
  label: string;
  value: unknown;
  onChange: (value: Record<string, unknown>) => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(props.value ?? {}, null, 2));
  const [error, setError] = useState<string | null>(null);
  const lastEmitted = useRef(props.value);
  useEffect(() => {
    if (props.value !== lastEmitted.current) {
      setText(JSON.stringify(props.value ?? {}, null, 2));
      lastEmitted.current = props.value;
      setError(null);
    }
  }, [props.value]);
  return (
    <label className={`ctl ctl-json${error ? " has-error" : ""}`}>
      <span>
        {props.label}
        {error && <em className="json-error"> — {error}</em>}
      </span>
      <textarea
        spellCheck={false}
        rows={Math.min(10, Math.max(2, text.split("\n").length))}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          try {
            const parsed = JSON.parse(e.target.value) as Record<string, unknown>;
            lastEmitted.current = parsed;
            setError(null);
            props.onChange(parsed);
          } catch {
            setError("invalid JSON");
          }
        }}
      />
    </label>
  );
}

export function Section(props: { title: string; count?: number; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="section">
      <button type="button" className="section-head" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="section-caret">{open ? "▾" : "▸"}</span>
        {props.title}
        {props.count !== undefined && <span className="section-count">{props.count}</span>}
      </button>
      {open && <div className="section-body">{props.children}</div>}
    </div>
  );
}

export function Row(props: { children: ReactNode; onRemove: () => void }) {
  return (
    <div className="row">
      {props.children}
      <button type="button" className="row-remove" title="remove" onClick={props.onRemove}>
        ×
      </button>
    </div>
  );
}

export function AddButton(props: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="btn btn-add" onClick={props.onClick}>
      + {props.label}
    </button>
  );
}

/** A primitive reference: a name picked from the registry + a JSON params sub-editor. */
export function PrimitiveRefEditor(props: {
  label: string;
  names: string[];
  value: PrimitiveRef;
  onChange: (value: PrimitiveRef) => void;
}) {
  return (
    <div className="primitive-ref">
      <SelectInput
        label={props.label}
        value={props.value.name}
        options={props.names.includes(props.value.name) ? props.names : [props.value.name, ...props.names]}
        onChange={(name) => props.onChange({ ...props.value, name })}
      />
      <JsonInput
        label="params"
        value={props.value.params ?? {}}
        onChange={(params) => props.onChange({ ...props.value, params })}
      />
    </div>
  );
}

export function RefListEditor(props: {
  title: string;
  names: string[];
  items: PrimitiveRef[];
  onChange: (items: PrimitiveRef[]) => void;
}) {
  const update = (index: number, next: PrimitiveRef) =>
    props.onChange(props.items.map((item, i) => (i === index ? next : item)));
  return (
    <div className="ref-list">
      {/* Index keys: removing a row above one with an unparsed JSON draft resets that
          draft to the (correct) shifted value — accepted for v1; defs stay consistent. */}
      {props.items.map((item, index) => (
        <Row key={index} onRemove={() => props.onChange(props.items.filter((_, i) => i !== index))}>
          <PrimitiveRefEditor
            label={`${props.title} ${index + 1}`}
            names={props.names}
            value={item}
            onChange={(next) => update(index, next)}
          />
        </Row>
      ))}
      <AddButton
        label={props.title}
        onClick={() =>
          props.onChange([...props.items, { name: props.names[0] ?? "", params: {} }])
        }
      />
    </div>
  );
}
